import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Copy, Loader2, Send, Eye, Save } from "lucide-react";
import { copyText } from "@/lib/clipboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const t = localStorage.getItem("grc_admin_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

const inputCls =
  "mt-1 w-full rounded-xl border border-linen-border focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20 p-2.5 text-sm text-navy bg-white";
const labelCls = "block text-[11px] font-bold uppercase tracking-[0.16em] text-ink-500";

export const AnnualCheckPanel = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);

  const load = () =>
    api.get("/admin/annual-check").then((r) => {
      setData(r.data);
      setForm(r.data.settings);
    }).catch(() => {});

  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy("save");
    try {
      const { data: d } = await api.put("/admin/annual-check", { ...form, ...patch });
      setForm(d.settings);
      toast.success("Campaign settings saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that.");
    } finally {
      setBusy("");
    }
  };

  const send = async (kind, dryRun, bdmId = null) => {
    setBusy(`${kind}${dryRun ? "-preview" : ""}${bdmId || ""}`);
    try {
      const { data: d } = await api.post("/admin/annual-check/send", {
        kind, dry_run: dryRun, bdm_id: bdmId,
      });
      if (dryRun) {
        setPreview(d);
        toast.success(`${d.count} partner${d.count === 1 ? "" : "s"} would get this`);
      } else {
        const sent = (d.recipients || []).filter((r) => r.sent).length;
        toast.success(`Sent to ${sent} of ${d.count}`);
        setPreview(null);
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send that.");
    } finally {
      setBusy("");
    }
  };

  if (!data || !form) return null;
  const { season, totals, partners, page_url: pageUrl } = data;

  return (
    <div className="rounded-3xl bg-white border border-linen-border shadow-soft overflow-hidden" data-testid="admin-annual-check-panel">
      <div className="bg-navy text-white px-6 py-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-lime">
              <CalendarCheck size={14} /> {season.label}
            </div>
            <h3 className="mt-1.5 font-heading font-medium text-xl">The Annual Rate Check</h3>
            <p className="mt-1 text-xs text-white/65 max-w-xl leading-relaxed">
              The named yearly campaign. A fixed season is the headline push; partners keep getting
              anniversary prompts between seasons, so nobody waits ten months for their check.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Status</div>
            <div className="text-sm font-semibold" data-testid="admin-annual-status">
              {season.open
                ? `Open · ${season.days_left} days left`
                : season.phase === "before"
                  ? (season.enabled ? `Opens ${season.opens_on}` : `Scheduled ${season.opens_on} (off)`)
                  : "Closed"}
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-5 border-b border-linen-border">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 items-end">
          <label className="sm:col-span-1">
            <span className={labelCls}>Season year</span>
            <input
              type="number"
              value={form.year}
              onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
              className={inputCls}
              data-testid="admin-annual-year"
            />
          </label>
          <label className="sm:col-span-1">
            <span className={labelCls}>Opens</span>
            <input
              type="date"
              value={form.opens_on}
              onChange={(e) => setForm({ ...form, opens_on: e.target.value })}
              className={inputCls}
              data-testid="admin-annual-opens"
            />
          </label>
          <label className="sm:col-span-1">
            <span className={labelCls}>Closes</span>
            <input
              type="date"
              value={form.closes_on}
              onChange={(e) => setForm({ ...form, closes_on: e.target.value })}
              className={inputCls}
              data-testid="admin-annual-closes"
            />
          </label>
          <label className="sm:col-span-2">
            <span className={labelCls}>Consumer headline</span>
            <input
              type="text"
              value={form.headline}
              onChange={(e) => setForm({ ...form, headline: e.target.value })}
              className={inputCls}
              data-testid="admin-annual-headline"
            />
          </label>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            onClick={() => save({})}
            disabled={busy === "save"}
            data-testid="admin-annual-save"
            className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-4 py-2 bg-navy text-white hover:bg-[#173a5f] disabled:opacity-60"
          >
            {busy === "save" ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
          </button>
          <button
            onClick={() => save({ enabled: !form.enabled })}
            data-testid="admin-annual-toggle"
            className={`inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-4 py-2 ${
              form.enabled ? "bg-lime text-navy-deep hover:brightness-95" : "bg-linen text-navy hover:bg-linen-border"
            }`}
          >
            {form.enabled ? "Campaign is ON" : "Campaign is OFF"}
          </button>
          <button
            onClick={async () => {
              const ok = await copyText(pageUrl);
              toast[ok ? "success" : "error"](ok ? "Campaign page link copied" : "Copy it manually");
            }}
            data-testid="admin-annual-copy-page"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal hover:text-navy"
          >
            <Copy size={12} /> {pageUrl}
          </button>
        </div>
        <p className="mt-3 text-[11px] text-ink-500 leading-relaxed">
          Turning the campaign ON opens the season between those dates: partner dashboards switch to
          campaign mode, every unchecked name becomes due, and the guidance engine puts it at the top
          of their day.
        </p>
      </div>

      <div className="px-6 py-5 border-b border-linen-border">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: "Partners", value: totals.partners, testid: "admin-annual-partners" },
            { label: "Running it", value: totals.running, testid: "admin-annual-running" },
            { label: "Not started", value: totals.not_started, testid: "admin-annual-notstarted" },
            { label: "Checked", value: totals.checked, testid: "admin-annual-checked" },
            { label: "Remaining", value: totals.remaining, testid: "admin-annual-remaining" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl bg-linen/60 border border-linen-border p-3" data-testid={s.testid}>
              <div className="text-xl font-heading font-medium text-navy leading-none">{s.value}</div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.14em] text-ink-500 font-semibold">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {["kickoff", "nudge"].map((kind) => (
            <div key={kind} className="inline-flex items-center gap-1.5">
              <button
                onClick={() => send(kind, true)}
                disabled={busy.startsWith(kind)}
                data-testid={`admin-annual-preview-${kind}`}
                className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-linen text-navy hover:bg-linen-border disabled:opacity-60"
              >
                <Eye size={12} /> Preview {kind === "kickoff" ? "season kickoff" : "mid-season nudge"}
              </button>
            </div>
          ))}
          {data.settings.kickoff_sent_at ? (
            <span className="text-[11px] text-ink-500">
              Kickoff last sent {String(data.settings.kickoff_sent_at).slice(0, 10)}
            </span>
          ) : null}
          {data.settings.nudge_sent_at ? (
            <span className="text-[11px] text-ink-500">
              Nudge last sent {String(data.settings.nudge_sent_at).slice(0, 10)}
            </span>
          ) : null}
        </div>

        {preview ? (
          <div className="mt-4 rounded-2xl border border-linen-border bg-linen/50 p-4" data-testid="admin-annual-preview">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-xs font-semibold text-navy">
                {preview.count} recipient{preview.count === 1 ? "" : "s"} for the {preview.kind}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPreview(null)}
                  className="text-xs text-ink-500 hover:text-navy"
                  data-testid="admin-annual-preview-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(`Send the ${preview.kind} to ${preview.count} partner(s) now?`)) {
                      send(preview.kind, false);
                    }
                  }}
                  disabled={busy.startsWith(preview.kind) || preview.count === 0}
                  data-testid="admin-annual-send-real"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-navy text-white hover:bg-[#173a5f] disabled:opacity-60"
                >
                  {busy.startsWith(preview.kind) ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  Send for real
                </button>
              </div>
            </div>
            <ul className="mt-3 space-y-1">
              {(preview.recipients || []).map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 text-[11px]">
                  <span className="text-navy font-semibold">{r.name || r.email}</span>
                  <span className="text-ink-500">{r.email}</span>
                  <span className="text-ink-500">{r.checked}/{r.total} checked</span>
                </li>
              ))}
              {preview.count === 0 ? (
                <li className="text-[11px] text-ink-500">Nobody qualifies for this send right now.</li>
              ) : null}
            </ul>
          </div>
        ) : null}
      </div>

      <div className="px-6 py-5">
        <p className="text-[11px] uppercase tracking-widest text-ink-400 font-bold">By partner</p>
        {partners.length === 0 ? (
          <p className="mt-2 text-sm text-ink-500" data-testid="admin-annual-no-partners">No partners yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-linen-border border-y border-linen-border" data-testid="admin-annual-partner-rows">
            {partners.map((p) => (
              <li key={p.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`admin-annual-row-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-navy text-sm truncate">{p.name || p.email}</div>
                  <div className="text-[11px] text-ink-500">
                    {p.total === 0 ? "No names on their list yet" : `${p.checked} of ${p.total} checked · ${p.remaining} to go`}
                  </div>
                </div>
                <div className="w-24 h-1.5 rounded-full bg-linen overflow-hidden shrink-0">
                  <div className="h-full rounded-full bg-teal" style={{ width: `${p.pct}%` }} />
                </div>
                <button
                  onClick={() => {
                    if (window.confirm(`Send the season kickoff email to ${p.name || p.email} only?`)) {
                      send("kickoff", false, p.id);
                    }
                  }}
                  disabled={!p.email || busy.includes(p.id)}
                  data-testid={`admin-annual-send-one-${p.id}`}
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1 bg-linen text-navy hover:bg-linen-border disabled:opacity-50"
                >
                  {busy.includes(p.id) ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Email
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AnnualCheckPanel;
